import React from 'react';
import PageBanner from '../../components/PageBanner';
import SEO from '../../components/SEO';
import SectionSidebar from '../../components/SectionSidebar';
import { Container, Typography, Box, Grid, Chip } from '@mui/material';
import MapIcon from '@mui/icons-material/Map';
import FlightIcon from '@mui/icons-material/Flight';
import TrainIcon from '@mui/icons-material/Train';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import DirectionsIcon from '@mui/icons-material/Directions';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import NearMeIcon from '@mui/icons-material/NearMe';
import ExploreIcon from '@mui/icons-material/Explore';

const GOLD = '#d4af37';

const routes = [
  {
    Icon: FlightIcon,
    gradient: ['#3b82f6', '#2563eb'] as [string, string],
    from: 'Calicut International Airport',
    distance: '45 km',
    time: '1 hour',
    steps: [
      'Head north on Airport Road',
      'Merge onto NH66 toward Kozhikode',
      'Continue on NH66 for approximately 40 km',
      'Take exit toward Muchukunnu / Anakkulam–Purakkad Road from Kollam–Pisharikavu Temple',
      'Turn right onto Anakkulam–Purakkad Road and continue for 4.8 km',
      'Temple is 200 m on the left',
    ],
  },
  {
    Icon: TrainIcon,
    gradient: ['#22c55e', '#16a34a'] as [string, string],
    from: 'Koyilandy Railway Station',
    distance: '8 km',
    time: '20 minutes',
    steps: [
      'Exit station and head toward Koyilandy town',
      'Take Koyilandy–Vatakara Road (NH 66)',
      'Continue for approximately 8 km',
      'Turn right from Kollam–Pisharikavu Temple onto Anakkulam–Purakkad Road and continue for 4.8 km',
      'Temple is 200 m on the left',
    ],
  },
];

const landmarks = [
  { text: 'Near Muchukunnu Junction (Main landmark)', Icon: LocationOnIcon },
  { text: 'Muchukunnu Akalappuzha Road', Icon: LocationOnIcon },
  { text: 'Close to Moodadi area', Icon: NearMeIcon },
  { text: 'Accessible via Koyilandy-Vatakara Road', Icon: DirectionsIcon },
];

export default function ContactMap() {
  return (
    <Box sx={{ bgcolor: '#f8fafb', minHeight: '100vh' }}>
      <SEO title="Map & Directions — Kottayil Temple | മാപ്പ്" description="Find directions to Kottayil Temple (Kotta-Kovilakam Kshethram) at Muchukunnu, Kozhikode. Interactive map, route guidance, and nearby landmarks." path="/contact/map" keywords="Kottayil temple directions, temple map, how to reach Kottayil temple, Muchukunnu location, കൊട്ടയിൽ ക്ഷേത്രം മാപ്പ്, വഴി" />
      <PageBanner title="Map & Directions" />
      <Container maxWidth="lg" sx={{ mt: { xs: -4, md: -6 }, position: 'relative', zIndex: 2, pb: 10 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={8} lg={9}>

            {/* HERO */}
            <Box sx={{
              borderRadius: '28px', overflow: 'hidden', mb: 4,
              boxShadow: '0 20px 60px rgba(0,0,0,0.08)',
              background: 'linear-gradient(135deg, #064e3b 0%, #047857 40%, #10b981 100%)',
              position: 'relative',
            }}>
              <Box sx={{ position: 'absolute', top: -50, right: -50, width: 220, height: 220, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)' }} />
              <Box sx={{ position: 'absolute', bottom: -30, left: -30, width: 160, height: 160, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.05) 0%, transparent 70%)' }} />
              <Box sx={{ position: 'absolute', inset: 0, opacity: 0.04, backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '24px 24px' }} />

              <Box sx={{ px: { xs: 3, sm: 4 }, py: { xs: 4, sm: 5 }, position: 'relative', zIndex: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mb: 2.5 }}>
                  <Box sx={{
                    width: 64, height: 64, borderRadius: '20px',
                    background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: '2px solid rgba(255,255,255,0.2)', boxShadow: '0 12px 32px rgba(0,0,0,0.15)',
                  }}>
                    <MapIcon sx={{ fontSize: 32, color: '#fff' }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 900, fontSize: { xs: '1.6rem', sm: '2rem' }, color: '#fff', lineHeight: 1.15, mb: 0.5, textShadow: '0 2px 12px rgba(0,0,0,0.2)' }}>
                      Map & Directions
                    </Typography>
                    <Typography sx={{ fontSize: { xs: '0.82rem', sm: '0.9rem' }, color: 'rgba(255,255,255,0.65)', fontWeight: 500 }}>
                      Find us easily with detailed directions from major landmarks
                    </Typography>
                  </Box>
                </Box>
                <Typography sx={{ fontSize: { xs: '0.88rem', sm: '0.95rem' }, color: 'rgba(255,255,255,0.55)', lineHeight: 1.9, maxWidth: 560, mb: 2.5 }}>
                  Muchukunnu Kottayil Sree Shiva Temple is well-connected by road, rail, and air. Use the map and step-by-step directions below to plan your visit.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  <Chip icon={<ExploreIcon sx={{ fontSize: '16px !important', color: '#10b981 !important' }} />} label="Interactive Maps" sx={{ bgcolor: 'rgba(255,255,255,0.95)', color: '#064e3b', fontWeight: 700, fontSize: '0.78rem', height: 34, borderRadius: '12px', '& .MuiChip-icon': { ml: '8px' } }} />
                  <Chip icon={<DirectionsIcon sx={{ fontSize: '16px !important', color: '#6ee7b7 !important' }} />} label="Step-by-Step" sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.9)', fontWeight: 700, fontSize: '0.78rem', height: 34, borderRadius: '12px', border: '1px solid rgba(255,255,255,0.15)', '& .MuiChip-icon': { ml: '8px' } }} />
                </Box>
              </Box>
            </Box>

            {/* EMBEDDED MAPS */}
            <Grid container spacing={2.5} sx={{ mb: 4 }}>
              {[
                { name: 'Kottayil Temple', src: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3907.8!2d75.51488!3d11.79765!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ba688f70ba4cc09%3A0xa1db14313acaad96!2sMuchukunnu%20Kottayil%20Sree%20Shiva%20Temple!5e0!3m2!1sen!2sin!4v1234567890', gradient: ['#f59e0b', '#d97706'] },
                { name: 'Kovilakam Temple', src: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3907.8!2d75.67279!3d11.479135!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ba689a95971377f%3A0xe8693ab59eea155c!2sKovilakam%20Temple!5e0!3m2!1sen!2sin!4v1234567890', gradient: ['#ef4444', '#dc2626'] },
              ].map((m) => (
                <Grid item xs={12} md={6} key={m.name}>
                  <Box sx={{ bgcolor: '#fff', borderRadius: '24px', overflow: 'hidden', border: '1px solid rgba(0,0,0,0.06)', boxShadow: '0 4px 24px rgba(0,0,0,0.04)', height: '100%' }}>
                    <Box sx={{ background: `linear-gradient(135deg, ${m.gradient[0]}, ${m.gradient[1]})`, px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.2 }}>
                      <Box sx={{ width: 30, height: 30, borderRadius: '10px', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <LocationOnIcon sx={{ fontSize: 16, color: '#fff' }} />
                      </Box>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: '#fff' }}>{m.name}</Typography>
                    </Box>
                    <iframe
                      title={`${m.name} Location`}
                      src={m.src}
                      width="100%"
                      height="350"
                      style={{ border: 0, display: 'block' }}
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                    />
                  </Box>
                </Grid>
              ))}
            </Grid>

            {/* ROUTE CARDS */}
            <Grid container spacing={2.5} sx={{ mb: 4 }}>
              {routes.map((r, idx) => {
                const RIcon = r.Icon;
                const [gS, gE] = r.gradient;
                return (
                  <Grid item xs={12} key={idx}>
                    <Box sx={{
                      bgcolor: '#fff', borderRadius: '24px', overflow: 'hidden',
                      border: '1px solid rgba(0,0,0,0.06)', boxShadow: '0 4px 24px rgba(0,0,0,0.04)',
                      transition: 'all 400ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                      '&:hover': { transform: 'translateY(-6px)', boxShadow: `0 20px 60px rgba(0,0,0,0.1)` },
                    }}>
                      <Box sx={{ background: `linear-gradient(135deg, ${gS}, ${gE})`, px: 3, py: 2, position: 'relative', overflow: 'hidden' }}>
                        <Box sx={{ position: 'absolute', top: -15, right: -15, width: 80, height: 80, borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }} />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, position: 'relative', zIndex: 2, flexWrap: 'wrap' }}>
                          <Box sx={{ width: 42, height: 42, borderRadius: '14px', background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(255,255,255,0.25)' }}>
                            <RIcon sx={{ fontSize: 22, color: '#fff' }} />
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', color: '#fff' }}>From {r.from}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Chip icon={<DirectionsIcon sx={{ fontSize: '14px !important', color: '#fff !important' }} />} label={r.distance} sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 700, fontSize: '0.72rem', height: 28, borderRadius: '10px', '& .MuiChip-icon': { ml: '6px' } }} />
                            <Chip icon={<AccessTimeIcon sx={{ fontSize: '14px !important', color: '#fff !important' }} />} label={r.time} sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 700, fontSize: '0.72rem', height: 28, borderRadius: '10px', '& .MuiChip-icon': { ml: '6px' } }} />
                          </Box>
                        </Box>
                      </Box>
                      <Box sx={{ p: { xs: 2.5, sm: 3 } }}>
                        {r.steps.map((s, i) => (
                          <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.2, py: 0.5 }}>
                            <Box sx={{ width: 22, height: 22, borderRadius: '8px', background: `linear-gradient(135deg, ${gS}18, ${gS}08)`, border: `1px solid ${gS}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: 0.1 }}>
                              <Typography sx={{ fontSize: '0.6rem', fontWeight: 800, color: gS }}>{i + 1}</Typography>
                            </Box>
                            <Typography sx={{ fontSize: '0.78rem', color: '#94a3b8', lineHeight: 1.6 }}>{s}</Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>

            {/* LANDMARKS FOOTER */}
            <Box sx={{
              background: 'linear-gradient(145deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
              borderRadius: '24px', p: { xs: 3, sm: 3.5 }, position: 'relative', overflow: 'hidden',
              border: '1px solid rgba(255,255,255,0.05)', boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            }}>
              <Box sx={{ position: 'absolute', inset: 0, opacity: 0.03, backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
              <Box sx={{ position: 'absolute', top: -30, right: -30, width: 120, height: 120, borderRadius: '50%', background: `radial-gradient(circle, ${GOLD}12 0%, transparent 70%)` }} />
              <Box sx={{ position: 'relative', zIndex: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                  <Box sx={{ width: 40, height: 40, borderRadius: '12px', background: `linear-gradient(135deg, ${GOLD}, #b8941f)`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 6px 20px ${GOLD}30` }}>
                    <LocationOnIcon sx={{ fontSize: 20, color: '#fff' }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.88rem', md: '1rem' }, color: '#fff', lineHeight: 1.2 }}>Nearby Landmarks</Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.35)', fontWeight: 500 }}>Reference points near the temple</Typography>
                  </Box>
                </Box>
                <Grid container spacing={1.5}>
                  {landmarks.map((l, i) => {
                    const LIcon = l.Icon;
                    return (
                      <Grid item xs={12} sm={6} key={i}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.8, px: 1.5, borderRadius: '12px', transition: 'all 250ms', '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' } }}>
                          <Box sx={{ width: 28, height: 28, borderRadius: '8px', bgcolor: `${GOLD}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <LIcon sx={{ fontSize: 14, color: GOLD }} />
                          </Box>
                          <Typography sx={{ fontSize: '0.76rem', color: 'rgba(255,255,255,0.5)', fontWeight: 500, lineHeight: 1.5 }}>{l.text}</Typography>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            </Box>

          </Grid>
          <Grid item xs={12} md={4} lg={3}>
            <SectionSidebar sectionKey="contact" activePath="/contact/map" />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
